import { globalMessageHandler } from "common";
import { CalendarEventItem } from "features/events/eventApi";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  duplicateEvent as apiDuplicateEvent,
  groupEditEvents as apiGroupEditEvents,
  groupEventActive as apiGroupEventActive,
  groupEventCancel as apiGroupEventCancel,
  groupEventDelete as apiGroupEventDelete,
  groupEventGroupActive as apiGroupEventGroupActive,
  groupEventGroupCancel as apiGroupEventGroupCancel, // NEW
  groupEventGroupDelete as apiGroupEventGroupDelete, // NEW
  groupShift as apiGroupShift,
  dragDropUpdate,
  groupEdit,
} from "./eventEditorApi";

export type EditMode = "dragDropUpdate" | "singleEdit" | "groupEdit" | null;
export type ViewMode = "normal" | "minimal";

export type EditorActionFn = ((params: any) => Promise<any>) | null;

export interface DragDropUpdateParams {
  event_id: number;
  new_event_group_id: number;
}

export interface GroupEditParams {
  event_group_ids: number[];
  new_start_time?: string;
  new_boat_id?: number;
  new_program_id?: number;
}

export interface GroupShiftParams {
  group_ids: number[];
  minutes_offset: number;
}

export interface GroupEditEventsParams {
  event_ids: number[];
  limits?: any;
  variants?: any;
  seating_layouts?: any;
  private?: boolean;
}

export interface DuplicateEventParams {
  event_id: number;
  new_start_time: string;
}

export interface EventEditorContextValue {
  editMode: EditMode;
  actionFn: EditorActionFn;
  selectedEvents: number[];
  selectedEventGroups: number[];
  firstSelectedEventProgramId: number | null;
  firstSelectedGroupBoatId: number | null;
  groupEditMode: "group" | "event" | null;
  modalOpen: boolean;
  loadingState:
    | undefined
    | "groupEventActive"
    | "groupEventGroupActive"
    | "groupEventCancel"
    | "groupEventGroupCancel"
    | "duplicateEvent"
    | "groupShift"
    | "groupEditEvents"
    | "groupEventGroupDelete"
    | "groupEventDelete";
  setEditMode: (mode: EditMode, actionFn?: EditorActionFn) => void;
  selectEvent: (eventId: number, programId: number) => void;
  deselectEvent: (eventId: number) => void;
  selectEventGroup: (groupId: number, boatId: number) => void;
  deselectEventGroup: (groupId: number) => void;
  openModal: () => void;
  closeModal: () => void;
  resetSelection: () => void;
  // Wrapped API functions without input parameters;
  // They use the selected events (or groups) from state and display confirmation dialogs.
  dragDropUpdate: (params: DragDropUpdateParams) => Promise<any>;
  groupEdit: (params: GroupEditParams) => Promise<any>;
  groupShift: () => Promise<any>;
  groupEditEvents: () => Promise<any>;
  groupEventCancel: () => Promise<any>;
  groupEventActive: () => Promise<any>;
  groupEventGroupCancel: () => Promise<any>;
  groupEventGroupActive: () => Promise<any>;
  groupEventDelete: (force?: boolean) => Promise<any>;
  groupEventGroupDelete: (force?: boolean) => Promise<any>;
  duplicateEvent: () => Promise<any>;
  // Bulk-selection functions
  selectSameEvents: () => void;
  selectSameGroups: () => void;
  selectSameEventsSameTime: (time: string) => void;
  selectSameGroupsSameTime: (time: string) => void;
  cal?: any;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  groupActionsEnabled: boolean;
  selectionCount: number;
  isGroupMode: boolean;
}

export interface EventEditorProviderProps {
  children: ReactNode;
  items: CalendarEventItem[];
  cal?: any;
}

const EventEditorContext = createContext<EventEditorContextValue | undefined>(
  undefined
);

export const EventEditorProvider = ({
  children,
  items,
  cal,
}: EventEditorProviderProps) => {
  const [editMode, setEditModeState] = useState<EditMode>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("normal");
  const [actionFn, setActionFn] = useState<EditorActionFn>(null);
  const [selectedEvents, setSelectedEvents] = useState<number[]>([]);
  const [selectedEventGroups, setSelectedEventGroups] = useState<number[]>([]);
  const [firstSelectedEventProgramId, setFirstSelectedEventProgramId] =
    useState<number | null>(null);
  const [firstSelectedGroupBoatId, setFirstSelectedGroupBoatId] = useState<
    number | null
  >(null);
  const [modalOpen, setModalOpen] = useState(false);
  // loadingState is not a boolean but a string that indicates the current action.
  const [loadingState, setLoadingState] = useState<
    | undefined
    | "groupEventActive"
    | "groupEventGroupActive"
    | "groupEventCancel"
    | "groupEventGroupCancel"
    | "duplicateEvent"
    | "groupShift"
    | "groupEditEvents"
    | "groupEventGroupDelete"
    | "groupEventDelete"
  >(undefined);
  const [groupEditMode, setGroupEditMode] = useState<"group" | "event" | null>(
    null
  );

  const selectionCount =
    groupEditMode === "group"
      ? selectedEventGroups.length
      : selectedEvents.length;

  const isGroupMode = groupEditMode === "group";
  const groupActionsEnabled =
    selectionCount > 0 && !loadingState && editMode === "groupEdit";

  useEffect(() => {
    if (firstSelectedGroupBoatId) {
      setGroupEditMode("group");
    } else if (firstSelectedEventProgramId) {
      setGroupEditMode("event");
    } else {
      setGroupEditMode(null);
    }
  }, [firstSelectedGroupBoatId, firstSelectedEventProgramId]);

  const resetSelection = () => {
    setSelectedEvents([]);
    setSelectedEventGroups([]);
    setFirstSelectedEventProgramId(null);
    setFirstSelectedGroupBoatId(null);
  };

  const setEditMode = (mode: EditMode, action?: EditorActionFn) => {
    setEditModeState(mode);
    setActionFn(action ? action : null);
    resetSelection();
  };

  const selectEvent = (eventId: number, programId: number) => {
    setSelectedEvents(prev => {
      if (prev.length === 0) {
        setFirstSelectedEventProgramId(programId);
      }
      const newArr = prev.includes(eventId)
        ? prev.filter(p => p !== eventId)
        : [...prev, eventId];
      if (newArr.length === 0) {
        setFirstSelectedEventProgramId(null);
      }
      console.log("Selected events:", newArr);
      return newArr;
    });
  };

  const deselectEvent = (eventId: number) => {
    setSelectedEvents(prev => {
      const newArr = prev.filter(id => id !== eventId);
      if (newArr.length === 0) {
        setFirstSelectedEventProgramId(null);
      }
      return newArr;
    });
  };

  const selectEventGroup = (groupId: number, boatId: number) => {
    setSelectedEventGroups(prev => {
      if (prev.length === 0) {
        setFirstSelectedGroupBoatId(boatId);
      }
      const newArr = prev.includes(groupId)
        ? prev.filter(p => p !== groupId)
        : [...prev, groupId];
      if (newArr.length === 0) {
        setFirstSelectedGroupBoatId(null);
      }
      console.log("Selected event groups:", groupId, newArr);
      return newArr;
    });
  };

  const deselectEventGroup = (groupId: number) => {
    setSelectedEventGroups(prev => {
      const newArr = prev.filter(id => id !== groupId);
      if (newArr.length === 0) {
        setFirstSelectedGroupBoatId(null);
      }
      return newArr;
    });
  };

  // Bulk selection: select all events with stored program.
  const selectSameEvents = () => {
    if (firstSelectedEventProgramId === null) return;
    const matchingIds = items
      .filter(ev => ev.program_id === firstSelectedEventProgramId)
      .map(ev => ev.id);
    setSelectedEvents(matchingIds);
    console.log("Selected same events:", matchingIds);
  };

  // Bulk selection: select all groups with stored boat.
  const selectSameGroups = () => {
    if (firstSelectedGroupBoatId === null) return;
    const matchingGroupIds = Array.from(
      new Set(
        items
          .filter(ev => ev.boat_id === firstSelectedGroupBoatId)
          .map(ev => ev.event_group_id)
      )
    );
    setSelectedEventGroups(matchingGroupIds);
    console.log("Selected same groups:", matchingGroupIds);
  };

  // Toggle selection of all events sharing the same start time and program.
  const selectSameEventsSameTime = (time: string) => {
    if (firstSelectedEventProgramId === null) return;
    const matching = items.filter(
      ev =>
        ev.program_id === firstSelectedEventProgramId &&
        ev.start_time.substr(11, 5) === time
    );
    const matchingIds = matching.map(ev => ev.id);
    const allSelected = matchingIds.every(id => selectedEvents.includes(id));
    if (allSelected) {
      setSelectedEvents(prev => prev.filter(id => !matchingIds.includes(id)));
      console.log("Deselected same events with same time:", matchingIds);
    } else {
      setSelectedEvents(prev => Array.from(new Set([...prev, ...matchingIds])));
      console.log("Selected same events with same time:", matchingIds);
    }
  };

  // Toggle selection of all groups sharing the same start time and boat.
  const selectSameGroupsSameTime = (time: string) => {
    if (firstSelectedGroupBoatId === null) return;
    const matching = items.filter(
      ev =>
        ev.boat_id === firstSelectedGroupBoatId &&
        ev.start_time.substr(11, 5) === time
    );
    const matchingGroupIds = Array.from(
      new Set(matching.map(ev => ev.event_group_id))
    );
    const allSelected = matchingGroupIds.every(id =>
      selectedEventGroups.includes(id)
    );
    if (allSelected) {
      setSelectedEventGroups(prev =>
        prev.filter(id => !matchingGroupIds.includes(id))
      );
      console.log("Deselected same groups with same time:", matchingGroupIds);
    } else {
      setSelectedEventGroups(prev =>
        Array.from(new Set([...prev, ...matchingGroupIds]))
      );
      console.log("Selected same groups with same time:", matchingGroupIds);
    }
  };

  const openModal = () => {
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  // New API wrappers with confirmation and loadingState.
  const groupEventActive = async () => {
    globalMessageHandler.confirm(
      { message: "Biztosan aktiválod az összes eseményt?" },
      async () => {
        setLoadingState("groupEventActive");
        const res = await apiGroupEventActive(selectedEvents);
        setLoadingState(undefined);
        if (res.success) {
          cal?.current?.refresh();
          globalMessageHandler.snack({
            severity: "success",
            message: `Sikeresen aktiválva. ${res.data.activated} esemény.`,
          });
        } else {
          globalMessageHandler.snack({
            severity: "error",
            message: res.error?.message,
          });
        }
      }
    );
  };

  const groupEventGroupActive = async () => {
    globalMessageHandler.confirm(
      { message: "Biztosan aktiválod az összes eseménycsoportot?" },
      async () => {
        setLoadingState("groupEventGroupActive");
        const res = await apiGroupEventGroupActive(selectedEventGroups);
        setLoadingState(undefined);
        if (res.success) {
          cal?.current?.refresh();
          globalMessageHandler.snack({
            severity: "success",
            message: `Sikeresen aktiválva. ${res.data.groups_activated} csoport.`,
          });
        } else {
          globalMessageHandler.snack({
            severity: "error",
            message: res.error?.message,
          });
        }
      }
    );
  };

  const groupEventCancel = async () => {
    globalMessageHandler.confirm(
      { message: "Biztosan törlöd/visszavonod az eseményeket?" },
      async () => {
        setLoadingState("groupEventCancel");
        const res = await apiGroupEventCancel(selectedEvents);
        setLoadingState(undefined);
        if (res.success) {
          cal?.current?.refresh();
          globalMessageHandler.snack({
            severity: "success",
            message: `Sikeresen lemondva. ${res.data.canceled} esemény.`,
          });
        } else {
          globalMessageHandler.snack({
            severity: "error",
            message: res.error?.message,
          });
        }
      }
    );
  };

  const groupEventGroupCancel = async () => {
    globalMessageHandler.confirm(
      { message: "Biztosan lemondod az eseménycsoportokat?" },
      async () => {
        setLoadingState("groupEventGroupCancel");
        const res = await apiGroupEventGroupCancel(selectedEventGroups);
        setLoadingState(undefined);
        if (res.success) {
          cal?.current?.refresh();
          globalMessageHandler.snack({
            severity: "success",
            message: `Sikeresen lemondva. ${res.data.groups_canceled} csoport.`,
          });
        } else {
          globalMessageHandler.snack({
            severity: "error",
            message: res.error?.message,
          });
        }
      }
    );
  };

  const duplicateEvent = async () => {
    globalMessageHandler.confirm(
      { message: "Biztosan duplikálod az eseményt?" },
      async () => {
        setLoadingState("duplicateEvent");
        // Assuming selectedEvents[0] is the event to duplicate and that new_start_time is derived from state/calender.
        const eventToDuplicate = selectedEvents[0];
        // For this example we assume new_start_time is available in state or cal.
        const newStartTime = cal?.current?.getNewStartTime
          ? cal.current.getNewStartTime()
          : "";
        const res = await apiDuplicateEvent({
          event_id: eventToDuplicate,
          new_start_time: newStartTime,
        });
        setLoadingState(undefined);
        if (res.success) {
          cal?.current?.refresh();
          globalMessageHandler.snack({
            severity: "success",
            message: `Esemény duplikálása sikeres.`,
          });
        } else {
          globalMessageHandler.snack({
            severity: "error",
            message: res.error?.message,
          });
        }
      }
    );
  };

  const groupShift = async () => {
    globalMessageHandler.confirm(
      { message: "Biztosan módosítod a kijelölt csoportok indulási idejét?" },
      async () => {
        setLoadingState("groupShift");
        // Here we assume selectedEventGroups holds relevant groups for shift.
        const res = await apiGroupShift({
          group_ids: selectedEventGroups,
          minutes_offset: 15,
        });
        // minutes_offset is hardcoded for example—adjust as needed.
        setLoadingState(undefined);
        if (res.success) {
          cal?.current?.refresh();
          globalMessageHandler.snack({
            severity: "success",
            message: `Csoportos eltolás sikeres.`,
          });
        } else {
          globalMessageHandler.snack({
            severity: "error",
            message: res.error?.message,
          });
        }
      }
    );
  };

  const groupEditEvents = async () => {
    globalMessageHandler.confirm(
      { message: "Biztosan módosítod a kijelölt eseményeket?" },
      async () => {
        setLoadingState("groupEditEvents");
        const res = await apiGroupEditEvents({
          event_ids: selectedEvents,
          limits: {},
        });
        // adjust payload as needed; here limits is empty for example.
        setLoadingState(undefined);
        if (res.success) {
          cal?.current?.refresh();
          globalMessageHandler.snack({
            severity: "success",
            message: `Események szerkesztése sikeres.`,
          });
        } else {
          globalMessageHandler.snack({
            severity: "error",
            message: res.error?.message,
          });
        }
      }
    );
  };

  // New function: Delete events (using selectedEvents from state)
  const groupEventDelete = async (force?: boolean) => {
    return apiGroupEventDelete(selectedEvents, force);
  };

  // New function: Delete event groups (using selectedEventGroups from state)
  const groupEventGroupDelete = async (force?: boolean) => {
    //setLoadingState("groupEventGroupDelete");
    return apiGroupEventGroupDelete(selectedEventGroups, force);
  };

  return (
    <EventEditorContext.Provider
      value={{
        editMode,
        actionFn,
        selectedEvents,
        selectedEventGroups,
        firstSelectedEventProgramId,
        firstSelectedGroupBoatId,
        groupEditMode,
        modalOpen,
        loadingState,
        setEditMode,
        selectEvent,
        deselectEvent,
        selectEventGroup,
        deselectEventGroup,
        openModal,
        closeModal,
        resetSelection,
        selectSameEvents,
        selectSameGroups,
        selectSameEventsSameTime,
        selectSameGroupsSameTime,
        dragDropUpdate,
        groupEdit,
        groupShift,
        groupEditEvents,
        groupEventCancel,
        groupEventActive,
        groupEventGroupCancel,
        groupEventGroupActive,
        duplicateEvent,
        groupEventDelete,
        groupEventGroupDelete,
        cal,
        viewMode,
        setViewMode,
        groupActionsEnabled,
        isGroupMode,
        selectionCount,
      }}
    >
      {children}
    </EventEditorContext.Provider>
  );
};

export const useEventEditorContext = (): EventEditorContextValue => {
  const context = useContext(EventEditorContext);
  if (context === undefined) {
    //By matching the structure of EventEditorContextValue, your fallback ensures no destructuring or function calls break even if the context is undefined.
    return {
      editMode: undefined as any,
      actionFn: () => {},
      selectedEvents: [],
      selectedEventGroups: [],
      firstSelectedEventProgramId: null,
      firstSelectedGroupBoatId: null,
      groupEditMode: null,
      modalOpen: false,
      loadingState: undefined,
      setEditMode: () => {},
      selectEvent: () => {},
      deselectEvent: () => {},
      selectEventGroup: () => {},
      deselectEventGroup: () => {},
      openModal: () => {},
      closeModal: () => {},
      resetSelection: () => {},
      dragDropUpdate: async () => {},
      groupEdit: async () => {},
      groupShift: async () => {},
      groupEditEvents: async () => {},
      groupEventCancel: async () => {},
      groupEventActive: async () => {},
      groupEventGroupCancel: async () => {},
      groupEventGroupActive: async () => {},
      groupEventDelete: async () => {},
      groupEventGroupDelete: async () => {},
      duplicateEvent: async () => {},
      selectSameEvents: () => {},
      selectSameGroups: () => {},
      selectSameEventsSameTime: () => {},
      selectSameGroupsSameTime: () => {},
      cal: undefined,
      viewMode: undefined as any,
      setViewMode: () => {},
      groupActionsEnabled: false,
      selectionCount: 0,
      isGroupMode: false,
    } as any;
  }

  return context;
};
